// Polygon helpers operating on 2D points in the world XZ plane.
//
// IMPORTANT: opengeometry.Polygon expects CW outer ring and CCW holes
// (verified against poly-wall.ts:233-234). signedAreaXZ returns
// negative for CW. Standard math/GIS convention is the opposite —
// do not "fix" this.

export type Point2 = [number, number]; // [x, z]

export const POLYGON_EPSILON = 1e-6;
export const POLYGON_AREA_EPSILON = 1e-6;

export type Winding = "CW" | "CCW";

export function pointsEqualXZ(a: Point2, b: Point2, eps = POLYGON_EPSILON): boolean {
  return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;
}

/** Shoelace area on the XZ plane. Positive = CCW, negative = CW. */
export function signedAreaXZ(ring: Point2[]): number {
  let area = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const cur = ring[i];
    const nxt = ring[(i + 1) % ring.length];
    area += cur[0] * nxt[1] - nxt[0] * cur[1];
  }
  return area / 2;
}

export function orientationXZ(a: Point2, b: Point2, c: Point2): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

export function onSegmentXZ(a: Point2, b: Point2, p: Point2, eps = POLYGON_EPSILON): boolean {
  return (
    p[0] <= Math.max(a[0], b[0]) + eps &&
    p[0] >= Math.min(a[0], b[0]) - eps &&
    p[1] <= Math.max(a[1], b[1]) + eps &&
    p[1] >= Math.min(a[1], b[1]) - eps
  );
}

export function segmentsIntersectXZ(a1: Point2, a2: Point2, b1: Point2, b2: Point2): boolean {
  const o1 = orientationXZ(a1, a2, b1);
  const o2 = orientationXZ(a1, a2, b2);
  const o3 = orientationXZ(b1, b2, a1);
  const o4 = orientationXZ(b1, b2, a2);

  const o1Zero = Math.abs(o1) <= POLYGON_EPSILON;
  const o2Zero = Math.abs(o2) <= POLYGON_EPSILON;
  const o3Zero = Math.abs(o3) <= POLYGON_EPSILON;
  const o4Zero = Math.abs(o4) <= POLYGON_EPSILON;

  if (o1Zero && onSegmentXZ(a1, a2, b1)) return true;
  if (o2Zero && onSegmentXZ(a1, a2, b2)) return true;
  if (o3Zero && onSegmentXZ(b1, b2, a1)) return true;
  if (o4Zero && onSegmentXZ(b1, b2, a2)) return true;

  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
}

export function hasSelfIntersectionXZ(ring: Point2[]): boolean {
  const n = ring.length;
  for (let i = 0; i < n; i += 1) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 1; j < n; j += 1) {
      const sharesStart = j === i;
      const sharesEnd = j === (i + 1) % n;
      const wraps = i === 0 && j === n - 1;
      if (sharesStart || sharesEnd || wraps) continue;
      const b1 = ring[j];
      const b2 = ring[(j + 1) % n];
      if (segmentsIntersectXZ(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

/** Drop consecutive duplicate points and any trailing point that closes the
 *  ring, returning a "unique vertex" list. Caller passes either an open ring
 *  or one that already closes — either way the result is the canonical
 *  unique-vertex form (no closing duplicate). */
export function dedupeAndAutoClose(ring: Point2[], eps = POLYGON_EPSILON): Point2[] {
  const out: Point2[] = [];
  for (const p of ring) {
    const last = out[out.length - 1];
    if (!last || !pointsEqualXZ(last, p, eps)) {
      out.push([p[0], p[1]]);
    }
  }
  if (out.length > 1 && pointsEqualXZ(out[0], out[out.length - 1], eps)) {
    out.pop();
  }
  return out;
}

export function ensureWinding(ring: Point2[], desired: Winding): Point2[] {
  const area = signedAreaXZ(ring);
  // Positive area = CCW, negative = CW.
  const isCCW = area > 0;
  const wantCCW = desired === "CCW";
  return isCCW === wantCCW ? ring.slice() : ring.slice().reverse();
}

/** Crossing-number point-in-polygon test on the XZ plane. Boundary points may
 *  return either true or false; callers needing strict-interior should
 *  combine this with an edge-distance check. */
export function pointInPolygonXZ(p: Point2, ring: Point2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0], zi = ring[i][1];
    const xj = ring[j][0], zj = ring[j][1];
    const intersects =
      (zi > p[1]) !== (zj > p[1]) &&
      p[0] < ((xj - xi) * (p[1] - zi)) / (zj - zi + 0) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** True if every vertex of `inner` lies inside `outer` AND no edge of `inner`
 *  crosses any edge of `outer`. Both rings are unique-vertex (no closing dup). */
export function ringStrictlyInside(inner: Point2[], outer: Point2[]): boolean {
  for (const v of inner) {
    if (!pointInPolygonXZ(v, outer)) return false;
  }
  for (let i = 0; i < inner.length; i += 1) {
    const a1 = inner[i];
    const a2 = inner[(i + 1) % inner.length];
    for (let j = 0; j < outer.length; j += 1) {
      const b1 = outer[j];
      const b2 = outer[(j + 1) % outer.length];
      if (segmentsIntersectXZ(a1, a2, b1, b2)) return false;
    }
  }
  return true;
}

/** True if any vertex of one ring lies inside the other, OR any pair of edges
 *  crosses. Both rings are unique-vertex (no closing dup). */
export function ringsOverlap(a: Point2[], b: Point2[]): boolean {
  for (const v of a) {
    if (pointInPolygonXZ(v, b)) return true;
  }
  for (const v of b) {
    if (pointInPolygonXZ(v, a)) return true;
  }
  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (segmentsIntersectXZ(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

/** Average of all points. Returns [0, 0] for empty input. */
export function centroidXZ(ring: Point2[]): Point2 {
  if (ring.length === 0) return [0, 0];
  let sx = 0;
  let sz = 0;
  for (const p of ring) {
    sx += p[0];
    sz += p[1];
  }
  return [sx / ring.length, sz / ring.length];
}

/** Translate every point. Returns a new array. */
export function translatePointsXZ(ring: Point2[], dx: number, dz: number): Point2[] {
  return ring.map(([x, z]) => [x + dx, z + dz]);
}

/** Rotate every point around `anchor` by `angleRad` (right-handed about +Y,
 *  i.e. angle increases counter-clockwise when viewed from above). */
export function rotatePointsXZ(ring: Point2[], angleRad: number, anchor: Point2): Point2[] {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const [ax, az] = anchor;
  return ring.map(([x, z]) => {
    const dx = x - ax;
    const dz = z - az;
    return [ax + dx * cos - dz * sin, az + dx * sin + dz * cos];
  });
}

/** Closed polygon profile in the world XZ plane. `outerRing` is the outer
 *  boundary; `holes` are interior cutouts. Both are auto-closed at validation
 *  time and normalized to the winding `opengeometry.Polygon` expects (CW outer,
 *  CCW holes). Used by `Floor`, `Slab`, and any other extruded-profile element. */
export interface PolygonProfile {
  outerRing: Point2[];
  holes?: Point2[][];
}

export interface NormalizedPolygonProfile {
  outer: Point2[];
  holes: Point2[][];
}

function cloneRing(ring: Point2[]): Point2[] {
  return ring.map(([x, z]) => [x, z] as Point2);
}

/** Deep-clone a profile so caller mutations don't bleed into element state. */
export function clonePolygonProfile(profile: PolygonProfile): PolygonProfile {
  return {
    outerRing: cloneRing(profile.outerRing),
    holes: (profile.holes ?? []).map(cloneRing),
  };
}

/** Validate, dedupe/auto-close, and normalize winding of a polygon profile.
 *
 *  Throws on invalid input:
 *  - non-array / non-finite coordinates
 *  - <3 unique vertices after dedupe
 *  - zero-area (collinear) outer ring or hole
 *  - self-intersecting outer ring or hole
 *  - hole not strictly inside the outer ring
 *  - overlapping holes
 *
 *  `label` is prefixed onto error messages so the caller's element type is
 *  visible in the thrown message (e.g. "Floor: profile.outerRing
 *  self-intersects."). */
export function validateAndNormalizeProfile(
  profile: PolygonProfile,
  label: string,
): NormalizedPolygonProfile {
  const outerRaw = profile.outerRing ?? [];
  if (!Array.isArray(outerRaw)) {
    throw new Error(`${label}: profile.outerRing must be an array of [x, z] points.`);
  }

  for (let i = 0; i < outerRaw.length; i += 1) {
    const p = outerRaw[i];
    if (!Array.isArray(p) || p.length !== 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) {
      throw new Error(`${label}: profile.outerRing[${i}] must be a finite [x, z] tuple.`);
    }
  }

  const outerUnique = dedupeAndAutoClose(outerRaw as Point2[]);
  if (outerUnique.length < 3) {
    throw new Error(
      `${label}: profile.outerRing must have at least 3 unique vertices (got ${outerUnique.length}).`,
    );
  }

  if (Math.abs(signedAreaXZ(outerUnique)) <= POLYGON_AREA_EPSILON) {
    throw new Error(`${label}: profile.outerRing is degenerate (collinear or zero-area).`);
  }

  if (hasSelfIntersectionXZ(outerUnique)) {
    throw new Error(`${label}: profile.outerRing self-intersects.`);
  }

  const outer = ensureWinding(outerUnique, "CW");

  const holesNormalized: Point2[][] = [];
  const holesRaw = profile.holes ?? [];
  for (let h = 0; h < holesRaw.length; h += 1) {
    const ring = holesRaw[h];
    if (!Array.isArray(ring)) {
      throw new Error(`${label}: profile.holes[${h}] must be an array of [x, z] points.`);
    }
    for (let i = 0; i < ring.length; i += 1) {
      const p = ring[i];
      if (!Array.isArray(p) || p.length !== 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) {
        throw new Error(`${label}: profile.holes[${h}][${i}] must be a finite [x, z] tuple.`);
      }
    }

    const holeUnique = dedupeAndAutoClose(ring as Point2[]);
    if (holeUnique.length < 3) {
      throw new Error(
        `${label}: profile.holes[${h}] must have at least 3 unique vertices (got ${holeUnique.length}).`,
      );
    }
    if (Math.abs(signedAreaXZ(holeUnique)) <= POLYGON_AREA_EPSILON) {
      throw new Error(`${label}: profile.holes[${h}] is degenerate.`);
    }
    if (hasSelfIntersectionXZ(holeUnique)) {
      throw new Error(`${label}: profile.holes[${h}] self-intersects.`);
    }
    if (!ringStrictlyInside(holeUnique, outer)) {
      throw new Error(`${label}: profile.holes[${h}] is not strictly inside the outer ring.`);
    }

    holesNormalized.push(ensureWinding(holeUnique, "CCW"));
  }

  for (let i = 0; i < holesNormalized.length; i += 1) {
    for (let j = i + 1; j < holesNormalized.length; j += 1) {
      if (ringsOverlap(holesNormalized[i], holesNormalized[j])) {
        throw new Error(`${label}: profile.holes[${i}] and profile.holes[${j}] overlap.`);
      }
    }
  }

  return { outer, holes: holesNormalized };
}
